import { Box, Button, DialogActions, DialogContent, DialogTitle, Divider, Stack, TextField, Typography } from '@mui/material';
import { Trans } from 'react-i18next';
import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { electronics } from '../../../../utils/constants';
import { BpeAdaptorIcon } from '../../../Icons/Bits';
import { useAppSelector } from '../../../../redux/ReduxHooks';
import { selectAreDrivebaseCommandActionsDisabled } from '../../../../redux/RobotSlice';

interface BEEAdaptorCheckDialogProps {
  open: boolean;
  port?: number;
  onClose: () => void;
}

const BEEAdaptorCheckDialog = ({ open, port, onClose }: BEEAdaptorCheckDialogProps) => {
  const [value, setValue] = useState<string>('0');
  const { BPEadaptor } = electronics;

  const adapterData = useAppSelector((state) => (port != null ? state.robotData.beeAdapterData[port] ?? null : null));
  const commandActionsDisabled = useAppSelector(selectAreDrivebaseCommandActionsDisabled);
  const currentOutput = adapterData?.output ?? null;
  const settingsAtOpenRef = useRef<number | null>(null);
  const sentTestValueRef = useRef(false);

  // Reset snapshot when dialog closes; clear when opening so we capture fresh state for this session.
  useEffect(() => {
    if (!open) {
      settingsAtOpenRef.current = null;
      sentTestValueRef.current = false;
      return;
    }
    if (port == null) return;
    settingsAtOpenRef.current = null;
    sentTestValueRef.current = false;
  }, [open, port]);

  // Snapshot first known output after open (from polling / Redux) as the value to restore on close.
  useEffect(() => {
    if (!open || port == null) return;
    if (settingsAtOpenRef.current !== null) return;
    const output = adapterData?.output;
    if (typeof output === 'number') {
      settingsAtOpenRef.current = output;
    }
  }, [open, port, adapterData]);

  // Poll Bee adapter settings when dialog is open
  useEffect(() => {
    if (!open || port == null) return;
    const poll = () => window.robotService?.getBeeAdapterSettings?.(port);
    poll();
    const id = setInterval(poll, 100);
    return () => clearInterval(id);
  }, [open, port]);

  const handleClose = () => {
    const shouldRestore = sentTestValueRef.current || !commandActionsDisabled;
    if (shouldRestore && port != null && settingsAtOpenRef.current != null) {
      window.robotService?.setBeeAdapterSettings?.(port, settingsAtOpenRef.current);
    }
    onClose();
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newVal = event.target.value;
    if (newVal === '') {
      setValue('');
    } else if (newVal === '-') {
      setValue(newVal);
    } else {
      if (Number(newVal) >= BPEadaptor.MIN_VALUE && Number(newVal) <= BPEadaptor.MAX_VALUE) {
        setValue(newVal);
      }
    }
  };

  const handleSendValue = () => {
    if (port == null || commandActionsDisabled) return;
    const numericValue = Number(value);
    if (Number.isNaN(numericValue)) return;
    window.robotService?.setBeeAdapterSettings?.(port, numericValue);
    sentTestValueRef.current = true;
  };

  return (
    <>
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="center" spacing={0}>
          <Box
            component={BpeAdaptorIcon}
            aria-hidden
            sx={{
              width: 28,
              height: 28,
              display: 'block',
              color: 'primary.main',
              mr: 1,
            }}
          />
          <Box component="span" sx={{ ml: 0.5 }}>
            <Trans i18nKey={'BEEAdaptorCheckDialog.title'}>BPE Adaptor</Trans>
            {port != null && (
              <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>
                <Trans i18nKey="electronicCheckDialog.port" values={{ port }}>
                  (Port {{ port }})
                </Trans>
              </Typography>
            )}
          </Box>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ width: 500, height: 300 }}>
        <Stack justifyContent={'center'} alignItems={'center'} spacing={2}>
          <Typography textAlign={'center'}>
            <Trans i18nKey={'BEEAdaptorCheckDialog.main-text'}>Test the BPE Adaptor by sending a chosen value between -100 and 100:</Trans>
          </Typography>
          <Box display="flex" alignItems="center" position={'relative'} justifyContent="center">
            <Typography sx={{ mr: 1, position: 'absolute', left: -50 }}>
              <Trans i18nKey={'BEEAdaptorCheckDialog.value'}>Value:</Trans>
            </Typography>
            <TextField
              id="BPE-Adaptor-value"
              type="number"
              value={value}
              onChange={handleChange}
              slotProps={{ htmlInput: { min: BPEadaptor.MIN_VALUE, max: BPEadaptor.MAX_VALUE } }}
              sx={{ width: 100 }}
            />
          </Box>
          <Button variant="contained" onClick={handleSendValue} disabled={port == null || commandActionsDisabled}>
            <Trans i18nKey={'BEEAdaptorCheckDialog.button.send'}>Send</Trans>
          </Button>
          {currentOutput != null && (
            <Typography variant="body2" color="text.secondary">
              <Trans i18nKey="BEEAdaptorCheckDialog.current-output">Current output: {{ output: currentOutput }}</Trans>
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <Divider />
      <DialogActions>
        <Button variant="contained" autoFocus onClick={handleClose} sx={{ alignSelf: 'flex-end' }}>
          <Trans i18nKey="BEEAdaptorCheckDialog.button.close">Close</Trans>
        </Button>
      </DialogActions>
    </>
  );
};

export default BEEAdaptorCheckDialog;
